import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

import { ConfirmDialogComponent } from '@shared/components';
import {
  dynamicTextPlaceholder,
  HttpReportContext,
  StaticMessage,
} from '@shared/enums';
import { Constants, getStaticMessage } from '@shared/utils';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import { DprTitlePipe } from '@shared/pipes';
import { DownloadReport, ReportDownloadInProgress } from '@shared/models/dpr';
import { MatDialog } from '@angular/material/dialog';
import { FileService } from '@shared/services/files';
import {
  ToastNotificationService,
  SignalRService,
  RoutingService,
  GenericHttpClientService,
} from '@shared/services';
import { Router } from '@angular/router';

@UntilDestroy()
@Injectable()
export class DownloadDprService {
  constructor(
    private dialog: MatDialog,
    private toastNotificationService: ToastNotificationService,
    private signalRService: SignalRService,
    private genericHttpClientService: GenericHttpClientService,
    private dprTitlePipe: DprTitlePipe,
    private userDetailsFacade: UserDetailsFacade,
    private appFacade: AppFacade,
    private fileService: FileService,
    private router: Router,
    private routingService: RoutingService
  ) {}

  downloadOnline(downloadReport: DownloadReport): void {
    const formattedDprDisplayId = this.dprTitlePipe.transform(
      downloadReport.displayId
    );

    this.getReportDownloadInProgress(downloadReport.dprDetailId)
      .pipe(
        untilDestroyed(this),
        take(1),
        mergeMap((reportDownload) => {
          if (reportDownload) {
            this.displayDownloadInProgressMessage(formattedDprDisplayId);
            return EMPTY;
          }

          let notificationMessage = $localize`:@@shared.downloadDprService.txtDownloading:Generating report for DPR #(---)`;
          notificationMessage = notificationMessage.replace(
            dynamicTextPlaceholder,
            formattedDprDisplayId
          );
          const toastId = this.toastNotificationService.info(
            notificationMessage,
            true
          );

          this.appFacade.setReportDownload({
            dprDetailId: downloadReport.dprDetailId,
            toastId,
          });

          return this.genericHttpClientService.get<void>({
            context: HttpReportContext.GetDprReportDownload,
            args: {
              dprDetailId: downloadReport.dprDetailId,
              addWatermark: downloadReport.addWatermark,
              options: downloadReport.options,
            },
          });
        }),
        tap(() => {
          this.registerNotification(downloadReport.dprDetailId);
        }),
        catchError(() => {
          this.displayDownloadError(
            downloadReport.dprDetailId,
            downloadReport.displayId
          );
          return of(EMPTY);
        })
      )
      .subscribe();
  }

  downloadOffline(dprDetailsId: number | string, addWatermark: boolean): void {
    // We need to show a dialog when opening the print preview, because the print preview will always block the JS thread.
    // Even though technically it is a new tab, it is still on the same JS thread.
    // TODO(Improvement): Blocking the execution of JS for the user interface ruins the user experience,
    // and stops all animation and events firing, until you have closed the print preview.
    // Check if we could switch to use jsPDF (https://github.com/parallax/jsPDF)
    if (
      navigator.userAgent.toLowerCase().indexOf('chrome') > -1 &&
      navigator.vendor.toLowerCase().indexOf('google') > -1
    ) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        ...Constants.DialogOptions,
        data: {
          title: $localize`:@@shared.downloadDprService.titlePrintModeOn:Print mode on`,
          message: $localize`:@@shared.downloadDprService.txtPrintPreview:Please close the print preview option in order to use the application.`,
          hideAllButtons: true,
        },
      });
      this.openPrintWindow(dprDetailsId, addWatermark);

      //This is needed in order to make sure that there is no leftover in the localstorage
      if (!!localStorage.getItem('reportPageTabClosed')) {
        localStorage.removeItem('reportPageTabClosed');
      }

      // We are listening to the storage changed event. From that even we are extracting the "reportPageTabClosed" key
      // and inspecting it's value, if it's true that means that the report tab has been closed and we need
      // to close the dialog. The "reportPageTabClosed" key is being set in the ReportContainerComponent when the
      // tab is closed from the tabs X button
      addEventListener('storage', (event) => {
        if (event.key === 'reportPageTabClosed' && event.newValue === 'true') {
          confirmDialog.close();
          localStorage.removeItem('reportPageTabClosed');
        }
      });
    } else {
      this.openPrintWindow(dprDetailsId, addWatermark);
    }
  }
  //#region Private methods
  private openPrintWindow(
    dprDetailsId: number | string,
    addWatermark: boolean
  ): Window {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        [
          this.routingService.routePrefix,
          'report',
          this.routingService.subprojectIdFromCurrentRoute,
        ],
        { queryParams: { reportId: dprDetailsId, addWatermark } }
      )
    );

    // We need to open the print preview mode of the reports in a new window(not tab) so that
    // when the window is closed from the X button the focus is restored on the parent tab
    const reportPrintPreview = window.open(url, 'popup', 'fullscreen');
    if (
      reportPrintPreview.outerWidth < screen.availWidth ||
      reportPrintPreview.outerHeight < screen.availHeight
    ) {
      reportPrintPreview.moveTo(0, 0);
      reportPrintPreview.resizeTo(screen.availWidth, screen.availHeight);
    }
    return reportPrintPreview;
  }

  private getReportDownloadInProgress(
    dprDetailId: number
  ): Observable<ReportDownloadInProgress> {
    return this.appFacade.query.reportDownloadsInProgress$.pipe(
      map((reportDownloads) =>
        reportDownloads.find((x) => x.dprDetailId === dprDetailId)
      )
    );
  }

  private displayDownloadInProgressMessage(
    formattedDprDisplayId: string
  ): void {
    let dialogMessage = $localize`:@@shared.downloadDprService.txtDownloadInProgress:A report generation for the DPR #(---) is already in progress.`;
    dialogMessage = dialogMessage.replace(
      dynamicTextPlaceholder,
      formattedDprDisplayId
    );

    this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title: $localize`:@@shared.downloadDprService.titleDownloadInProgress:Generation in progress`,
        message: dialogMessage,
        buttonLabel: getStaticMessage(StaticMessage.OkButton),
        hideCancelButton: true,
      },
    });
  }

  private displayDownloadError(dprDetailId: number, displayId: number): void {
    this.getReportDownloadInProgress(dprDetailId)
      .pipe(
        untilDestroyed(this),
        take(1),
        tap((reportDownload) => {
          this.toastNotificationService.hide(reportDownload.toastId);
          this.appFacade.clearReportDownload(dprDetailId);

          let errorMessage = $localize`:@@shared.downloadDprService.errDownloading:Error while generating report for DPR #(---)`;
          errorMessage = errorMessage.replace(
            dynamicTextPlaceholder,
            this.dprTitlePipe.transform(displayId)
          );
          this.toastNotificationService.error(errorMessage);
        })
      )
      .subscribe();
  }

  private registerNotification(dprDetailId: number): void {
    this.signalRService.fileGenerated$
      .pipe(
        withLatestFrom(
          this.userDetailsFacade.query.accountId$,
          this.appFacade.query.isAppOnline$,
          this.getReportDownloadInProgress(dprDetailId)
        ),
        untilDestroyed(this),
        filter(
          ([fileGenerated, accountId, isAppOnline, reportDownload]) =>
            isAppOnline &&
            accountId === fileGenerated.AccountId &&
            fileGenerated.Key === dprDetailId &&
            !!reportDownload
        ),
        take(1),
        tap(([fileGenerated, _accountId, _isAppOnline, reportDownload]) => {
          this.fileService.download(
            fileGenerated.FileUrl,
            fileGenerated.FileName,
            fileGenerated.FileType
          );
          this.toastNotificationService.hide(reportDownload.toastId);
          this.appFacade.clearReportDownload(dprDetailId);
        })
      )
      .subscribe();
  }

  //#endregion
}
